import React, {
    createContext,
    ReactElement,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { HeadTag } from 'shared/components';
import DashboardTable from '../../home/manage-clients/dashboard/components/DefaultTable';
import DrawerWrapper from '../../home/manage-clients/dashboard/DrawerWrapper';
import { Box } from '@mui/material';
import { sendRequest } from 'modules/home/manage-packets/listing-packets/components/data/fetch';
import FilterSidebar from 'modules/home/manage-clients/dashboard/components/FilterSidebar';
import { useDashboardState } from 'shared/hooks/DashboardPage';
import { useDispatch, useSelector } from 'react-redux';
import { SocketActions } from 'state/socket/Action';
import { abortProtocol } from 'modules/home/manage-packets/listing-packets/components/data/Main';
import { APIManager } from 'shared/manager/APIManager';
import { useUpdateEffect } from 'react-use';

export const DashboardMeta = createContext<DashboardMetaProps | null>(null);

const DashboardPage = (): ReactElement => {
    const formikRef = useRef<any>(null);
    const dispatch = useDispatch();
    const [state, setState, setSearchValues] = useDashboardState();
    const [isRefreshDashboard, setRefreshDashboard] =
        React.useState<boolean>(false);
    const [isNetworkErr, setNetworkError] = React.useState<boolean>(false);
    const [isReduxInitializeFinish, setReduxInitializeFinish] =
        useState<boolean>(false);
    React.useState<boolean>(false);
    const [isNonRetry, setNonRetry] = React.useState<boolean>(false);
    const [isTableHeadingContentFocus, setTableHeadingContentFocus] =
        React.useState<boolean>(false);

    const socketState = useSelector(
        (stateRedux: any) => stateRedux.socketState
    );
    const fetchingTemplate = useSelector(
        (state: any) => state.baseAppState.fetchingTemplate
    );
    const initializeFromLogin = useSelector(
        (selectorState: any) =>
            selectorState.baseAppState.sidebarOptions.dashboard
                .initializeFromLogin
    );
    const initializeFromReload = useSelector(
        (selectorState: any) =>
            selectorState.baseAppState.sidebarOptions.dashboard
                .initializeFromReload
    );
    const resetInitialize = useSelector(
        (selectorState: any) =>
            selectorState.baseAppState.sidebarOptions.dashboard.resetInitialize
    );
    const globalTemplateEnabled = useSelector(
        (selectorState: any) =>
            selectorState.profileState.userInfo.globalDashboardTemplateEnabled
    );

    // const userInfo = useSelector((state: any) => state.profileState.userInfo);
    const socketEvent = React.useMemo(
        () => socketState.socketData.eventName,
        [socketState.socketData.eventName]
    );
    /**
     * effect to be used in formik initial setting on redux saved value
     */
    useLayoutEffect(() => {
        if (state.isReduxInitialize) {
            setState((prevState) => ({
                ...prevState,
                page:
                    state.reduxInitValues.offset &&
                    typeof state.reduxInitValues.offset === 'number'
                        ? state.reduxInitValues.offset - 1
                        : 0,
                packetStatus: state.reduxInitValues.packetStatus,
                packetType: state.reduxInitValues.packetType,
                sortBy: state.reduxInitValues.sort,
                rowsPerPage:
                    state.reduxInitValues.limit &&
                    typeof state.reduxInitValues.limit === 'number'
                        ? state.reduxInitValues.limit
                        : 10,
            }));

            setSearchValues(state.searchInitialValues);
            if (formikRef.current) {
                const { setFieldValue } = formikRef.current;

                setFieldValue(
                    'search_loan',
                    state.reduxInitValues.loanNumber ?? ''
                );
                setFieldValue(
                    'search_bin',
                    state.reduxInitValues.binNumber ?? ''
                );
                setFieldValue(
                    'search_client',
                    state.reduxInitValues.clientName ?? ''
                );
                setFieldValue(
                    'search_operator',
                    state.reduxInitValues.operatorId ?? ''
                );
                setFieldValue(
                    'search_cm9',
                    state.reduxInitValues.cm9Number ?? ''
                );
                setFieldValue(
                    'search_datepicker_uploaddate',
                    state.reduxInitValues.uploadDate
                );
                setFieldValue(
                    'search_datepicker_completeddate',
                    state.reduxInitValues.completedDate
                );
                setFieldValue(
                    'search_sladate',
                    state.reduxInitValues.slaDaysLeft ?? 0
                );
                setFieldValue(
                    'search_uploadage',
                    state.reduxInitValues.uploadAge ?? 0
                );
            }
            setReduxInitializeFinish(true);
        }
    }, [state.isReduxInitialize]);

    /**
     * Reset search values in the state.
     */
    const resetSearchValues = (): void => {
        setSearchValues({
            SLADate: '',
            binNumber: '',
            clientName: '',
            completedDate: '',
            escalatedDate: '',
            loanNumber: '',
            operatorId: '',
            packetStatus: '',
            packetType: '',
            uploadedDate: '',
            completedDateSearch: '',
        });
    };

    /**
     * Reset all state values.
     */
    const resetStateValues = (): void => {
        setState((prevState: any) => ({
            ...prevState,
            searchBy: '',
            sortBy: 'oldestToNew',
            textField: '',
            packetStatus: [],
            packetType: [],
        }));
    };

    /**
     * Update the data and related state variables.
     *
     * @param {any[]} newData - New data to update.
     * @param {number} newTotalRows - New total number of rows.
     * @param {any} formik - The formik object (optional).
     */
    const updateData = (
        newData: any[],
        newTotalRows: number,
        formik: any,
        isNetworkError: boolean
    ): void => {
        setState((prevState: any) => ({
            ...prevState,
            data: newData,
            totalRows: newTotalRows,
            isLoading: false,
        }));
        setNetworkError(isNetworkError);
        if (formik) {
            formik.setSubmitting(false);
        }
    };

    /**
     * Handle state changes for various fields.
     *
     * @param {'packetStatus' | 'packetType' | 'searchBy' | 'sortBy'} type - The type of state to update.
     * @param {React.SetStateAction<string | string[]>} value - The new value for the state.
     */
    const handleChange = (
        type: string,
        value: React.SetStateAction<any>
    ): void => {
        setState((prevState: any) => ({
            ...prevState,
            // Update the appropriate state based on the 'type' parameter
            [type]: typeof value === 'string' ? value : [...value],
            isLoading: true,
        }));

        sendRequest(
            formikRef.current?.values?.search_loan || '',
            formikRef.current?.values?.search_bin || '',
            formikRef.current?.values?.search_client || '',
            formikRef.current?.values?.search_operator || '',
            formikRef.current?.values?.search_cm9 || '',
            formikRef.current?.values?.search_datepicker_uploaddate || '',
            formikRef.current?.values?.search_datepicker_completeddate || '',
            formikRef.current?.values?.search_sladate || 0,
            formikRef.current?.values?.search_uploadage || 0,
            type === 'sortBy' ? (value as string) : state.sortBy,
            false,
            state.page + 1,
            state.rowsPerPage ?? 10,

            type === 'packetStatus' ? (value as string[]) : state.packetStatus,
            type === 'packetType' ? (value as string[]) : state.packetType,
            (
                newData: any[],
                newTotalRows: number,
                formik: any,
                isNetworkError: boolean
            ) => {
                updateData(newData, newTotalRows, formik, isNetworkError);
            }
        );
    };

    /**
     * Handle changes in the searchBy state.
     *
     * @param {string | React.ChangeEvent<HTMLSelectElement>} event - The event object or value.
     */
    const handleChangeSearchBy = (event: any): void => {
        setState((prevState: any) => ({
            ...prevState,
            page: 0,
        }));
        let newValue = '';

        if (typeof event === 'string') {
            handleChange('searchBy', event);
            newValue = event;
        } else if (event.target) {
            handleChange('searchBy', event.target.value);
            newValue = event.target.value;
        }

        setSearchValues({
            binNumber: '',
            clientName: '',
            loanNumber: '',
            operatorId: '',
            [newValue]: formikRef.current?.values?.search_packets || '',
        });
    };

    /**
     * Handle changes in the sortBy state.
     *
     * @param {string | React.ChangeEvent<HTMLSelectElement>} event - The event object or value.
     */
    const handleChangeSortBy = (event: any): void => {
        setState((prevState: any) => ({
            ...prevState,
            page: 0,
        }));
        const value = typeof event === 'string' ? event : event.target.value;
        handleChange('sortBy', value);

        const resetSearchValues: Record<string, string> = {
            SLADate: '',
            completedDate: '',
            escalatedDate: '',
            uploadedDate: '',
        };

        switch (value) {
            case 'dateCompleted':
                resetSearchValues.completedDate = 'Newest First';
                break;
            case 'desDateCompleted':
                resetSearchValues.completedDate = 'Oldest First';
                break;
            case 'dateOfUpload':
                resetSearchValues.uploadedDate = 'Newest First';
                break;
            case 'oldestToNew':
                resetSearchValues.uploadedDate = 'Oldest First';
                break;
            case 'daysLeftToSLA':
                resetSearchValues.SLADate = 'Oldest First';
                break;
            case 'desDaysLeftToSLA':
                resetSearchValues.SLADate = 'Newest First';
                break;
            case 'escalatedAge':
                resetSearchValues.escalatedDate = 'Newest First';
                break;
            case 'desEscalatedAge':
                resetSearchValues.escalatedDate = 'Oldest First';
                break;
            default:
                break;
        }

        setSearchValues(resetSearchValues);
    };

    /**
     * Executes the `handleChange` function with 'searchBy' type and the current value of searchBy state
     * whenever the 'page' state changes.
     */
    const [isStateFetching, setStateFetching] = useState<boolean>(false);

    React.useEffect(() => {
        if (
            fetchingTemplate === true &&
            state.initializeInDashboard === false &&
            (initializeFromLogin === false || initializeFromReload === false)
        ) {
            setStateFetching(true);
            return;
        }

        handleChange('searchBy', state.searchBy);
    }, [state.page, state.rowsPerPage]);

    useUpdateEffect(() => {
        setNonRetry(false);
    }, [state.page]);
    useLayoutEffect(() => {
        if (
            state.initializeInDashboard &&
            isStateFetching &&
            isReduxInitializeFinish
        ) {
            setReduxInitializeFinish(false);
            setStateFetching(false);
            handleChange('searchBy', state.searchBy);
        }
    }, [state.initializeInDashboard, isStateFetching, isReduxInitializeFinish]);

    React.useEffect(() => {
        if (socketEvent !== null) {
            if (
                socketEvent === 'packetStatus' ||
                socketEvent === 'operatorAction'
            ) {
                const socketPacketId = socketState.socketData.packetId;

                if (state.data.some((e) => e.actions.uuid === socketPacketId)) {
                    setRefreshDashboard(true);
                } else {
                    setNonRetry(true);
                }
            }
            dispatch(SocketActions.ResetSocketEvent({}));
        }
    }, [socketEvent]);
    const [isRefreshGlobalTemplate, setRefreshGlobalTemplate] =
        React.useState<boolean>(false);

    useUpdateEffect(() => {
        if (
            state.isReduxInitialize === false &&
            resetInitialize &&
            globalTemplateEnabled === false
        ) {
            setRefreshDashboard(true);
        } else if (
            state.isReduxInitialize === false &&
            resetInitialize === true &&
            globalTemplateEnabled === true
        ) {
            setRefreshGlobalTemplate(true);
        }
    }, [state.isReduxInitialize, resetInitialize, globalTemplateEnabled]);
    useLayoutEffect(() => {
        if (
            isRefreshGlobalTemplate &&
            resetInitialize === false &&
            globalTemplateEnabled === false
        ) {
            setRefreshDashboard(true);
            setRefreshGlobalTemplate(false);
        }
    }, [isRefreshGlobalTemplate, resetInitialize, globalTemplateEnabled]);
    // refresh on reset global template button
    // useUpdateEffect(() => {
    //     if (userInfo.globalDashboardTemplateEnabled === false) {
    //         setRefreshDashboard(true);
    //     }
    // }, [userInfo.globalDashboardTemplateEnabled]);
    /**
     * Executes the `handleChange` function with refresh from socket connection
     */
    React.useEffect(() => {
        if (isRefreshDashboard) {
            handleChange('searchBy', state.searchBy);
            setRefreshDashboard(false);
        }
    }, [isRefreshDashboard]);

    /**
     * Update search values based on changes in the textField state.
     * we have textField on other hand we also use formik.
     */
    React.useEffect(() => {
        setSearchValues({
            [state.searchBy]: state.textField,
        });
    }, [state.textField]);

    /**
     * Handle changes in the packet status checkbox.
     *
     * @param {React.ChangeEvent<HTMLInputElement>} event - The event object.
     * @returns {void}
     */
    const handleChangePacketStatus = (
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        let updatedPacketStatus: any[];

        if (typeof event === 'string') {
            updatedPacketStatus = event === '' ? [] : [event];
        } else {
            const data = event.target.value;

            if (data === 'viewall') {
                updatedPacketStatus = event.target.checked
                    ? []
                    : state.packetStatus;
            } else if (event.target.checked) {
                updatedPacketStatus = [...state.packetStatus, data];
            } else {
                updatedPacketStatus = state.packetStatus.filter(
                    (item: string) => item !== data
                );
            }
        }

        setState((prevState: any) => ({
            ...prevState,
            page: 0,
            packetStatus: updatedPacketStatus,
        }));

        handleChange('packetStatus', updatedPacketStatus);

        const formattedPacketStatus = updatedPacketStatus
            .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
            .join(', ');

        setSearchValues({
            packetStatus: formattedPacketStatus,
        });
    };

    /**
     * Handle changes in the packet type checkbox.
     *
     * @param {React.ChangeEvent<HTMLInputElement> | string} event - The event object or value.
     * @returns {void}
     */
    const handleChangePacketType = (
        event: React.ChangeEvent<HTMLInputElement> | string
    ): void => {
        let updatedPacketType: any[];

        if (typeof event === 'string') {
            updatedPacketType = event === '' ? [] : [event];
        } else {
            const data = event.target.value;

            if (data === 'viewall') {
                updatedPacketType = event.target.checked
                    ? []
                    : state.packetType;
            } else if (event.target.checked) {
                updatedPacketType = [...state.packetType, data];
            } else {
                updatedPacketType = state.packetType.filter(
                    (item) => item !== data
                );
            }
        }

        setState((prevState: any) => ({
            ...prevState,
            page: 0,
            packetType: updatedPacketType,
        }));
        handleChange('packetType', updatedPacketType);

        const formattedPacketType = updatedPacketType
            .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
            .join(', ');

        setSearchValues({
            packetType: formattedPacketType,
        });
    };

    /**
     * Handle reset action.
     *
     * @param {any} formik - The formik object.
     * @param {Function} callback - Callback function.
     * @returns {void}
     */
    const handleReset = (formik: any, callback: any): void => {
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        const revocation = () => {
            setState((prevState: any) => ({
                ...prevState,

                isLoading: true,
            }));
        };
        if (abortProtocol.protocol) {
            APIManager.target.addEventListener('revocation', revocation);
        }
        setState((prevState: any) => ({
            ...prevState,

            isLoading: true,
        }));
        sendRequest(
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            0,
            0,
            'oldestToNew',
            formik,
            state.page + 1,
            state.rowsPerPage ?? 10,
            [],
            [],
            (
                e: any[],
                totalRows: number,
                formik: any,
                isNetworkError: boolean
            ) => {
                APIManager.target.removeEventListener('revocation', revocation);
                updateData(e, totalRows, formik, isNetworkError);
                formik.setSubmitting(false);
                callback();
                resetSearchValues();
                resetStateValues();
            }
        );
    };

    const initialDashboardMeta = React.useMemo(
        () => ({
            setNonRetry,
            isNonRetry,
            isRefreshDashboard,
            setRefreshDashboard,
            isTableHeadingContentFocus,
            setTableHeadingContentFocus,
            isNetworkError: isNetworkErr,
            setNetworkError,
        }),
        [
            isNetworkErr,
            isNonRetry,
            isRefreshDashboard,
            isTableHeadingContentFocus,
        ]
    );

    return (
        <Box>
            <HeadTag title="Dashboard" />
            <DashboardMeta.Provider value={initialDashboardMeta}>
                <DrawerWrapper
                    Sidebar={FilterSidebar}
                    handleChangeSortBy={handleChangeSortBy}
                    handleChangeSearchBy={handleChangeSearchBy}
                    updateData={updateData}
                    handleChangePacketStatus={handleChangePacketStatus}
                    handleChangePacketType={handleChangePacketType}
                    handleReset={handleReset}
                    formikRef={formikRef}
                    state={state}
                    setState={setState}
                >
                    <DashboardTable
                        isNetworkError={isNetworkErr}
                        handleChangeSortBy={handleChangeSortBy}
                        handleChangeSearchBy={(
                            property: string
                            // search: string
                        ) => {
                            // We make this separated because different usecases, this only change
                            // to spesific searchBy with existing state
                            setState((prevState: any) => ({
                                ...prevState,
                                searchBy: property,
                                isLoading: true,
                            }));
                            sendRequest(
                                formikRef.current?.values?.search_loan || '',
                                formikRef.current?.values?.search_bin || '',
                                formikRef.current?.values?.search_client || '',
                                formikRef.current?.values?.search_operator ||
                                    '',
                                formikRef.current?.values?.search_cm9 || '',
                                formikRef.current?.values
                                    ?.search_datepicker_uploaddate || '',
                                formikRef.current?.values
                                    ?.search_datepicker_completeddate || '',
                                formikRef.current?.values?.search_sladate || 0,
                                formikRef.current?.values?.search_uploadage ||
                                    0,
                                state.sortBy,
                                false,
                                state.page + 1,
                                state.rowsPerPage ?? 10,
                                state.packetStatus,
                                state.packetType,
                                (
                                    e: any[],
                                    totalRows: number,
                                    formik: any,
                                    isNetworkError: boolean
                                ) => {
                                    updateData(
                                        e,
                                        totalRows,
                                        formik,
                                        isNetworkError
                                    );
                                }
                            );
                        }}
                        handleChangePacketStatus={handleChangePacketStatus}
                        handleChangePacketType={handleChangePacketType}
                        formikRef={formikRef}
                        updateSearch={(ev: any) => {
                            setState((prevState: any) => ({
                                ...prevState,
                                searchBy: ev,
                            }));
                        }}
                        setSearchValues={(ev: any) => {
                            setSearchValues(ev);
                        }}
                        state={state}
                        setState={setState}
                    />
                </DrawerWrapper>
            </DashboardMeta.Provider>
        </Box>
    );
};

export default DashboardPage;
